import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import ChatAxios from '../../services/api/chatAxios';

interface EnterpriseContactFormData {
  company_name: string;
  contact_name: string;
  contact_email: string;
  phone_number: string;
}

interface EnterpriseContactResponse {
  success: boolean;
  message: string;
  data?: any;
}

const extractSessionId = (payload: any): string | null => {
  if (!payload) return null;
  return (
    payload.session_id ||
    payload.sessionId ||
    payload.id ||
    payload?.data?.session_id ||
    payload?.data?.sessionId ||
    payload?.data?.id ||
    null
  );
};

const sendEnterpriseContact = async (data: EnterpriseContactFormData): Promise<EnterpriseContactResponse> => {
  const roomKey = process.env.REACT_APP_CHAT_ROOM_KEY || 'lottoreco_chat';

  console.log('Enterprise Contact Debug:', {
    roomKey,
    data,
    apiPayload: {
      customer_name: data.contact_name,
      customer_email: data.contact_email,
      company_name: data.company_name,
      phone_number: data.phone_number,
      room_key: roomKey,
      request_type: 'enterprise_contact'
    }
  });

  try {
    const userInfoToStore = {
      company_name: data.company_name,
      contact_name: data.contact_name,
      contact_email: data.contact_email,
      phone_number: data.phone_number,
      room_key: roomKey
    };
    localStorage.setItem('enterprise_contact_info', JSON.stringify(userInfoToStore));

    const sessionResponse = await ChatAxios.post('/chat/sessions', {
      customer_name: data.contact_name,
      customer_email: data.contact_email,
      company_name: data.company_name,
      phone_number: data.phone_number,
      room_key: roomKey,
      request_type: 'enterprise_contact'
    });

    const sessionId = extractSessionId(sessionResponse.data);
    if (!sessionId) {
      throw new Error('Failed to create session');
    }

    const messageResponse = await ChatAxios.post(`/sessions/${sessionId}/messages`, {
      message: `Enterprise Contact Request:\nCompany: ${data.company_name}\nContact: ${data.contact_name}\nEmail: ${data.contact_email}\nPhone: ${data.phone_number}\n\nThis is an enterprise inquiry for multi-store lottery management solutions.`
    });

    return {
      success: true,
      message: 'Enterprise contact request sent successfully',
      data: {
        session_id: sessionId,
        message_sent: messageResponse.data
      }
    };
  } catch (error: any) {
    console.error('Enterprise Contact Error:', {
      error,
      response: error.response,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
      code: error.code
    });

    if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please refresh the page and try again.');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied. Please contact support.');
    } else if (error.response?.status === 404) {
      throw new Error('Service temporarily unavailable. Please try again later.');
    } else if (error.response?.status === 429) {
      throw new Error('Too many requests. Please wait a moment and try again.');
    } else if (error.response?.status >= 500) {
      throw new Error('Server error. Please try again later.');
    } else if (error.code === 'NETWORK_ERROR' || !navigator.onLine) {
      throw new Error('Network error. Please check your connection and try again.');
    } else {
      const errorMessage = error.response?.data?.message || error.response?.data?.detail || error.message || 'Failed to send enterprise contact request. Please try again.';
      throw new Error(errorMessage);
    }
  }
};

export const useEnterpriseContact = () => {
  const [isOpen, setIsOpen] = useState(false);

  const enterpriseContactMutation = useMutation({
    mutationFn: sendEnterpriseContact,
    retry: (failureCount, error: any) => {
      if (error.message?.includes('Network error') ||
          error.message?.includes('Server error') ||
          error.response?.status >= 500) {
        return failureCount < 2;
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    onSuccess: () => {
      toast.success('Enterprise contact request sent! Our sales team will reach out shortly.');
      setIsOpen(false);
    },
    onError: (error: any) => {
      const errorMessage = error.message || 'Failed to send enterprise contact request. Please try again.';
      toast.error(errorMessage);
      console.error('Enterprise contact error:', error);
    },
  });

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSubmit = (data: EnterpriseContactFormData) => {
    enterpriseContactMutation.mutate(data);
  };

  return {
    isOpen,
    isPending: enterpriseContactMutation.isPending,
    isRetrying: enterpriseContactMutation.isPending && enterpriseContactMutation.failureCount > 0,
    handleOpen,
    handleClose,
    handleSubmit,
  };
};
